/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { DownOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BulkArchiveErrorResponse, Node, NodeFilters, PaginatedNodesResponse, Tag } from '@/client/';
import { useMessage } from '@/hooks/message';
import { useBulkArchiveNodesMutation } from '@/hooks/mutation/useBulkArchiveNodesMutation';
import { useAuthorization } from '@/hooks/useAuthorization';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { CreateFleetFromNodesWizard } from '@/pages/Nodes/CreateFleetFromNodesWizard/CreateFleetFromNodesWizard';
import { NodesColumns } from '@/pages/Nodes/getNodesColumns';
import { NodeFilter } from '@/pages/Nodes/NodesHeader';
import { NodesTable } from '@/pages/Nodes/NodesTable';
import { ToggleMaintenanceModeModal } from '@/pages/Nodes/ToggleMaintenanceModeModal';
import { ToggleStateModal } from '@/pages/Nodes/ToggleStateModal';
import { Button } from '@/primitives/Button';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Space } from '@/primitives/Space';
import { TableProps } from '@/primitives/Table';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { setJobWizardSelectedNodes } from '@/state/jobWizardStore';
import { UpdateParams } from '@/utils/params';

export type NodesListProps = {
  tenantID: string;
  tags: Tag[] | undefined;
  onParamsChange: UpdateParams<Node, Partial<NodeFilters>>;
  nodesState: NodeFilter;
  nodesData: PaginatedNodesResponse;
  onAssignConfiguration: (node: Node) => void;
  getNodeConfigurationPath: (nodeConfigId: string) => string;
};

const pendingColumns: NodesColumns[] = [
  'hostname',
  'lastSeen',
  'operatingSystem',
  'dateCreated',
  'assignConfiguration',
];

const configuredColumns: NodesColumns[] = [
  'hostname',
  'tags',
  'appliedOperationalState',
  'appliedNodeConfigMode',
  'connectivityState',
  'operatingSystem',
  'lastSeen',
  'appliedConfiguration',
  'nodeVersion',
];

const archivedColumns: NodesColumns[] = ['hostname', 'operatingSystem', 'lastSeen', 'dateCreated'];
const actionButtonStyle: React.CSSProperties = { height: '36px' };

export function NodesList({
  tenantID,
  tags,
  onParamsChange,
  nodesState,
  nodesData,
  onAssignConfiguration,
  getNodeConfigurationPath,
}: NodesListProps) {
  const [message] = useMessage();
  const { t } = useMimicTranslation('nodes');
  const navigate = useNavigate();
  const canManageNodes = useAuthorization('admin');
  const [selectedNodes, setSelectedNodes] = useState<Node[]>([]);
  const [isToggleStateModalOpen, setIsToggleStateModalOpen] = useState(false);
  const [isToggleMaintenanceModeModalOpen, setIsToggleMaintenanceModeModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isCreateFleetWizardOpen, setIsCreateFleetWizardOpen] = useState(false);
  const [noSystemProfileInfo, setNoSystemProfileInfo] = useState<boolean>(false);
  const [sameOs, setSameOs] = useState<boolean>(true);
  const [sameSubtenant, setSameSubtenant] = useState<boolean>(true);
  const [showDifferentOsError, setShowDifferentOsError] = useState<boolean | undefined>();
  const [showDifferentSubtenantError, setShowDifferentSubtenantError] = useState<boolean | undefined>();

  const getVisibleColumns = () => {
    if (nodesState === NodeFilter.ARCHIVED) return archivedColumns;
    if (nodesState === NodeFilter.PENDING) return pendingColumns;
    return configuredColumns;
  };
  const visibleColumns = getVisibleColumns();
  const showSelection = nodesState !== NodeFilter.ARCHIVED;

  const selectedNodeIds = selectedNodes.map((node) => node.id);
  const hasAuditNodes = selectedNodes.some((node) => node.appliedNodeConfigMode === 'audit');
  const hasNonDisconnectedNodes = selectedNodes.some((node) => node.connectivityState !== 'disconnected');
  const archiveSelectedDisabled = selectedNodes.length === 0 || hasNonDisconnectedNodes;
  const createFleetDisabled = selectedNodes.length === 0 || !sameOs || !sameSubtenant;
  const { mutateAsync: bulkArchiveNodes, isPending: isArchivePending } = useBulkArchiveNodesMutation({
    onSuccess: () => {
      message.success(t('archiveSelectedNodesModal.success'));
      setSelectedNodes([]);
      setJobWizardSelectedNodes([]);
      setIsArchiveModalOpen(false);
    },
    onError: (error) => {
      const errorBody = error.body as BulkArchiveErrorResponse | undefined;
      const validationDetails = errorBody?.errors?.map((err) => err.message).join(', ');
      message.error(validationDetails || errorBody?.message || t('archiveSelectedNodesModal.errorFallback'));
    },
  });

  const rowSelection: TableProps<Node>['rowSelection'] = {
    type: 'checkbox',
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedNodeIds,
    onChange: (_selectedKeys: React.Key[], selectedRows: Node[]) => {
      const selectedOses = selectedRows.map((node) => node.operatingSystem);
      const selectedSubtenants = selectedRows.map((node) => node.subtenant?.id);
      const uniqueOses = new Set(selectedOses);
      const uniqueSubtenants = new Set(selectedSubtenants);
      const newSameOsVal = uniqueOses.size === 1 || selectedRows.length === 0;
      const newSameSubtenantVal = uniqueSubtenants.size === 1 || selectedRows.length === 0;
      setSameOs(newSameOsVal);
      setSameSubtenant(newSameSubtenantVal);
      setSelectedNodes(selectedRows);
      setJobWizardSelectedNodes(selectedRows);
      setNoSystemProfileInfo(selectedRows.some((node) => !node.operatingSystem));
      if (newSameOsVal) {
        setShowDifferentOsError(false);
      }
      if (newSameSubtenantVal) {
        setShowDifferentSubtenantError(false);
      }
    },
    getCheckboxProps: (node: Node) => ({
      name: node.name,
    }),
    renderCell: (_value, node: Node, _, originNode) => {
      // Clone the original node and add data-testid
      if (React.isValidElement(originNode)) {
        return React.cloneElement(originNode, {
          ...(originNode.props as Record<string, unknown>),
          'data-testid': `checkbox-${node.id}`,
        } as React.HTMLAttributes<HTMLElement>);
      }
      return originNode;
    },
  };

  const noSystemProfileInfoMessage = noSystemProfileInfo ? t('banner.noSystemProfileInfo') : undefined;
  const selectionSubtenantWarning = !sameSubtenant ? t('banner.differentSubtenantWarning') : undefined;
  const selectionWarning = !sameOs ? t('banner.differentOSWarning') : undefined;
  const archiveSelectionWarning = hasNonDisconnectedNodes ? t('banner.onlyDisconnectedCanBeArchived') : undefined;
  const selectionError = showDifferentOsError ? t('banner.differentOSError') : undefined;
  const selectionSubtenantError = showDifferentSubtenantError ? t('banner.differentSubtenantError') : undefined;

  const warning = selectionWarning || selectionSubtenantWarning || archiveSelectionWarning;
  const error = selectionError || selectionSubtenantError;

  function openToggleStateModal() {
    setIsToggleStateModalOpen(selectedNodes.length > 0);
  }

  function openToggleMaintenanceModeModal() {
    setIsToggleMaintenanceModeModalOpen(selectedNodes.length > 0);
  }

  function configureSelectedNodes() {
    if (!sameOs) {
      setShowDifferentOsError(true);
      return;
    }
    if (!sameSubtenant) {
      setShowDifferentSubtenantError(true);
      return;
    }
    if (noSystemProfileInfo) {
      return;
    }
    navigate(`/tenants/${tenantID}/job-configure-nodes`);
  }

  function openArchiveSelectedModal() {
    setIsArchiveModalOpen(true);
  }

  function openCreateFleetWizard() {
    if (!sameOs) {
      setShowDifferentOsError(true);
      return;
    }
    if (!sameSubtenant) {
      setShowDifferentSubtenantError(true);
      return;
    }
    if (noSystemProfileInfo) {
      return;
    }
    setIsCreateFleetWizardOpen(true);
  }

  const nodeActionsMenu: MenuProps = {
    items: [
      {
        key: 'toggle-state',
        label: t('toggleState'),
      },
      {
        key: 'toggle-maintenance',
        label: t('toggleMaintenance'),
        disabled: hasAuditNodes,
      },
      {
        key: 'configure-nodes',
        label: <span data-testid="configure-nodes-action">{t('configureNodes')}</span>,
      },
      {
        key: 'create-fleet',
        label: t('createFleet'),
        disabled: createFleetDisabled,
      },
      {
        key: 'archive-selected',
        label: t('archiveSelected'),
        danger: true,
        disabled: archiveSelectedDisabled,
      },
    ],
    onClick: ({ key }) => {
      switch (key) {
        case 'toggle-state':
          openToggleStateModal();
          break;
        case 'toggle-maintenance':
          openToggleMaintenanceModeModal();
          break;
        case 'configure-nodes':
          configureSelectedNodes();
          break;
        case 'create-fleet':
          openCreateFleetWizard();
          break;
        case 'archive-selected':
          openArchiveSelectedModal();
          break;
        default:
          break;
      }
    },
  };

  let primaryAction;
  if (showSelection && canManageNodes) {
    primaryAction = (
      <Dropdown menu={nodeActionsMenu} dataTestId="node-bulk-actions-dropdown">
        <Button type="primary" style={actionButtonStyle} dataTestId="node-bulk-actions-button">
          {t('actions')} <DownOutlined style={{ fontSize: '10px' }} />
        </Button>
      </Dropdown>
    );
  }

  return (
    <>
      <NodesTable
        nodesData={nodesData}
        onParamsChange={onParamsChange}
        columns={visibleColumns}
        tags={tags}
        tenantID={tenantID}
        getNodeConfigurationPath={getNodeConfigurationPath}
        onAssignConfiguration={onAssignConfiguration}
        selectedNodeIds={showSelection ? selectedNodeIds : undefined}
        noSystemProfileInfoMessage={showSelection ? noSystemProfileInfoMessage : undefined}
        selectionWarning={showSelection ? warning : undefined}
        selectionError={showSelection ? error : undefined}
        rowSelection={showSelection ? rowSelection : undefined}
        primaryAction={primaryAction}
      />
      <ToggleStateModal
        selectedNodes={selectedNodes}
        open={isToggleStateModalOpen}
        onClose={() => setIsToggleStateModalOpen(false)}
        onSuccess={() => {
          setSelectedNodes([]);
          setIsToggleStateModalOpen(false);
        }}
      />
      <ToggleMaintenanceModeModal
        selectedNodes={selectedNodes}
        open={isToggleMaintenanceModeModalOpen}
        onClose={() => setIsToggleMaintenanceModeModalOpen(false)}
        onSuccess={() => {
          setSelectedNodes([]);
          setIsToggleMaintenanceModeModalOpen(false);
        }}
      />
      <Modal
        open={isArchiveModalOpen}
        onCancel={() => setIsArchiveModalOpen(false)}
        onOk={() => bulkArchiveNodes({ nodeIds: selectedNodeIds })}
        okText={t('archiveSelected')}
        cancelText={t('archiveSelectedNodesModal.cancel')}
        okButtonProps={{ danger: true, loading: isArchivePending }}
        confirmLoading={isArchivePending}
        destroyOnClose
      >
        <Space dir="vertical" size="md" width="100%">
          <Title level={4}>{t('archiveSelectedNodesModal.title')}</Title>
          <Text type="subtle">{t('archiveSelectedNodesModal.description', { count: selectedNodes.length })}</Text>
          <Space dir="vertical" size="xs" width="100%">
            {selectedNodes.slice(0, 5).map((node) => (
              <Text key={node.id} mono>
                {node.hostname || node.name || node.id}
              </Text>
            ))}
            {selectedNodes.length > 5 && (
              <Text type="subtle">{t('archiveSelectedNodesModal.andMore', { count: selectedNodes.length - 5 })}</Text>
            )}
          </Space>
        </Space>
      </Modal>
      <CreateFleetFromNodesWizard
        open={isCreateFleetWizardOpen}
        onClose={() => {
          setIsCreateFleetWizardOpen(false);
          setSelectedNodes([]);
          setJobWizardSelectedNodes([]);
        }}
        initialNodes={selectedNodes}
        tenantId={tenantID}
      />
    </>
  );
}
